// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { executeAwaitableHandlerChain, replaceInChain } from "@insite/client-framework/HandlerCreator";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import {
    chain,
    LoadProducts,
    LoadRealTimeInventory,
    LoadRealTimePrices,
} from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadProducts from "@insite/client-framework/Store/Data/Products/Handlers/LoadProducts";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";

export const XngLoadProducts: any = async (props: any) => {
    const parameter = props.parameter.getProductCollectionParameter;
    if (!parameter) {
        return false;
    }

    props.products = getProductsDataView(props.getState(), parameter).value;
    const productsDataViewState = getProductsDataView(props.getState(), parameter);

    if (productsDataViewState.isLoading) {
        props.dispatch({
            type: "Components/ProductInfoLists/SetWaitingForData",
            id: props.parameter.id,
            parameter: props.parameter.getProductCollectionParameter,
        });
        return false;
    }

    props.products = productsDataViewState.value;
    props.dataViewHasProducts = !!props.products;

    if (props.products && !props.parameter.forceLoad) {
        return;
    }

    let newParameter = parameter;
    if (parameter.filter === "recentlyViewed" && props.parameter.excludeProductId) {
        newParameter = { ...newParameter, excludeProductId: props.parameter.excludeProductId };
    }

    try {
        props.products = await executeAwaitableHandlerChain(loadProducts, newParameter, props);
    } catch (error) {
        props.errorMessage = error;
    }
};

export const XngLoadRealTimePrices: any = async (props: any) => {
    if (
        getSettingsCollection(props.getState()).customSettings &&
        (getSettingsCollection(props.getState()).customSettings as any).postalCodeLocationFinder.toLowerCase() ===
            "true" &&
        typeof window !== "undefined" &&
        !window.localStorage.getItem("postalCodeID")?.length &&
        !props.getState().context.session.isAuthenticated
    ) {
        return;
    }

    if (
        !props.productInfos ||
        props.productInfos.length === 0 ||
        (props.dataViewHasProducts && props.parameter.isShellEdit)
    ) {
        return;
    }

    props.dispatch(
        loadRealTimePricing({
            productPriceParameters: props.productInfos,
            onSuccess: realTimePricing => {
                props.dispatch({
                    type: "Components/ProductInfoLists/CompleteLoadRealTimePricing",
                    id: props.parameter.id,
                    realTimePricing,
                });
                props.pricingLoaded = true;
            },
            onError: () => {
                props.dispatch({
                    type: "Components/ProductInfoLists/FailedLoadRealTimePricing",
                    id: props.parameter.id,
                });
                props.pricingLoaded = true;
            },
            onComplete(realTimePricingProps) {
                if (realTimePricingProps.apiResult) {
                    this.onSuccess?.(realTimePricingProps.apiResult);
                } else if (realTimePricingProps.error) {
                    this.onError?.(realTimePricingProps.error);
                }
            },
        }),
    );

    if (getSettingsCollection(props.getState()).productSettings.inventoryIncludedWithPricing) {
        await waitFor(() => !!props.pricingLoaded);
    }
};

export const XngLoadRealTimeInventory: any = (props: any) => {
    if (
        getSettingsCollection(props.getState()).customSettings &&
        (getSettingsCollection(props.getState()).customSettings as any).postalCodeLocationFinder.toLowerCase() ===
            "true" &&
        typeof window !== "undefined" &&
        !window.localStorage.getItem("postalCodeID")?.length &&
        !props.getState().context.session.isAuthenticated
    ) {
        return;
    }
    if (
        !props.productInfos ||
        props.productInfos.length === 0 ||
        (props.dataViewHasProducts && props.parameter.isShellEdit)
    ) {
        return;
    }

    props.dispatch(
        loadRealTimeInventory({
            productIds: props.productInfos.map((o: any) => o.productId),
            onSuccess: realTimeInventory => {
                props.dispatch({
                    type: "Components/ProductInfoLists/CompleteLoadRealTimeInventory",
                    id: props.parameter.id,
                    realTimeInventory,
                });
            },
            onError: () => {
                props.dispatch({
                    type: "Components/ProductInfoLists/FailedLoadRealTimeInventory",
                    id: props.parameter.id,
                });
            },
            onComplete(realTimeInventoryProps) {
                if (realTimeInventoryProps.apiResult) {
                    this.onSuccess?.(realTimeInventoryProps.apiResult);
                } else if (realTimeInventoryProps.error) {
                    this.onError?.(realTimeInventoryProps.error);
                }
            },
        }),
    );
};

replaceInChain(chain, LoadProducts, XngLoadProducts);
replaceInChain(chain, LoadRealTimePrices, XngLoadRealTimePrices);
replaceInChain(chain, LoadRealTimeInventory, XngLoadRealTimeInventory);
